/**
 * CLIENT-SIDE AUTH STATE PROVIDER
 * * USE CASE: Wrapping the entire application to provide 'useAuth()' access.
 * * WHY: Bridges the gap between Server and Client. It takes the 'initialUser' 
 * from the Root Layout to prevent UI flickering and then listens for live 
 * events (like sign-outs or token refreshes) to keep the UI in sync.
 */

"use client";

import { createContext, useContext, useEffect, useState, ReactNode, JSX } from "react";
import { useRouter } from "next/navigation";
import { supabase } from "@/lib/supabase/client";
import { SignInFormData, SignUpFormData } from "@/models/data_models";
import type { SessionUser } from "@/lib/types";
import { toast } from "sonner";

interface BaseResponse {
  message?: string;
  error?: string;
  redirect?: string;
}

type UserResponse = SessionUser & BaseResponse;

interface AuthDataResponse extends BaseResponse {
  user: SessionUser;
}

// -----------------------------
// Types
// -----------------------------

interface AuthContextType {
  user: SessionUser | null;
  loading: boolean;
  signUp: (data: SignUpFormData) => Promise<void>;
  signIn: (data: SignInFormData) => Promise<void>;
  signOut: () => Promise<void>;
  updateUser: (newUser: SessionUser) => void;
  fetchUser: () => void;
  updatePassword: (data: { currentPassword: string; newPassword: string }) => Promise<void>;
  updateEmail: (data: { newEmail: string; password: string }) => Promise<void>;
  updateName: (data: { newFirstName: string; newLastName: string }) => Promise<void>;
  deleteAccount: (data: { password: string }) => Promise<void>;
  updatePreferences: (data: { newPreferences: Record<string, unknown> }) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// -----------------------------
// Context
// -----------------------------
const AuthContext = createContext<AuthContextType | null>(null);

// -----------------------------
// Provider
// -----------------------------
export function AuthProvider({ children, initialUser }: AuthProviderProps & { initialUser: SessionUser | null }): JSX.Element {
  const router = useRouter();
  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const [loading, setLoading] = useState(!initialUser);

  // Standardized Fetch Helper
  const handleAuthRequest = async <T extends BaseResponse>(
    url: string,
    method: string,
    body: object,
    fallbackError: string
  ): Promise<T> => {
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const result = (await response.json()) as T;

    if (!response.ok) {
      throw new Error(result.error || fallbackError);
    }

    return result;
  };

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      // Re-derive via the same getSessionUser() the server uses to seed
      // this context, rather than duplicating the tier-resolution join
      // client-side.
      const response = await fetch("/api/auth/session");
      const result = (await response.json()) as { user: SessionUser | null };
      setUser(result.user);
    } else {
      setUser(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Only fetch if the event is a SIGNED_IN or TOKEN_REFRESHED 
      // AND we don't already have the right user, or if they SIGNED_OUT
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user && (!user || user.id !== session.user.id)) {
          fetchUser();
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setLoading(false);
      }
    });

    return () => listener.subscription.unsubscribe();
  }, [user]);

  const signUp = async (data: SignUpFormData) => {
    try {
      setLoading(true);
      const result = await handleAuthRequest<AuthDataResponse>("/api/auth/sign-up", "POST", data, "Sign up failed.");
      setUser(result.user);
      // Not /sign-in — email confirmation is still pending at this point.
      router.push("/verify");
      router.refresh();
    } catch (error) {
      const err = error as Error;
      throw new Error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (data: SignInFormData) => {
    try {
      setLoading(true);
      const result = await handleAuthRequest<AuthDataResponse>("/api/auth/sign-in", "POST", { email: data.email, password: data.password }, "Sign in failed.");
      setUser(result.user);
      router.push("/dashboard");
      router.refresh();
    } catch (error) {
      const err = error as Error;
      throw new Error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      const result = await handleAuthRequest<BaseResponse>("/api/auth/sign-out", "POST", {}, "Sign out failed");
      if (result.redirect) {
        setUser(null);
        window.location.href = result.redirect;
      }
    } catch (error) {
      const err = error as Error;
      console.error("Sign out error:", err.message);
    }
  };

  const updateEmail = async ({ newEmail, password }: { newEmail: string; password: string }) => {
    try {
      const result = await handleAuthRequest<BaseResponse>("/api/user/update-email", "POST", { newEmail, password }, "Email change failed");
      toast.success(result.message || "Verification email sent", {
        description: "Please check your new email to confirm the change."
      });
    } catch (error) {
      const err = error as Error;
      toast.error(err.message);
      throw err;
    }
  };

  const updatePassword = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    try {
      const result = await handleAuthRequest<BaseResponse>("/api/user/update-password", "POST", { currentPassword, newPassword }, "Password change failed");
      toast.success(result.message || "Password updated");
    } catch (error) {
      const err = error as Error;
      toast.error(err.message);
      throw err;
    }
  };

  const updateName = async ({ newFirstName, newLastName }: { newFirstName: string; newLastName: string }) => {
    try {
      const result = await handleAuthRequest<UserResponse>("/api/user/update-name", "POST", { new_first_name: newFirstName, new_last_name: newLastName }, "Name change failed");
      // Merge rather than replace — the route only returns the updated
      // profile row, not tier/subscription, and a wholesale replace would
      // wipe those out of the context.
      setUser((prev) => (prev ? { ...prev, ...result } : prev));
      toast.success("Name updated successfully");
    } catch (error) {
      const err = error as Error;
      toast.error(err.message);
      throw err;
    }
  };

  const updatePreferences = async ({ newPreferences }: { newPreferences: Record<string, unknown> }) => {
    try {
      const result = await handleAuthRequest<UserResponse>("/api/user/update-preferences", "POST", { new_preferences: newPreferences }, "Preferences update failed");
      setUser((prev) => (prev ? { ...prev, ...result } : prev));
      toast.success("Preferences saved");
    } catch (error) {
      const err = error as Error;
      toast.error(err.message);
      throw err;
    }
  };

  const deleteAccount = async ({ password }: { password: string }) => {
    try {
      await handleAuthRequest<BaseResponse>("/api/user/delete-account", "POST", { password }, "Deletion failed");
      setUser(null);
      toast.success("Account deleted");
      window.location.href = "/sign-in?status=deleted";
    } catch (error) {
      const err = error as Error;
      toast.error(err.message);
      throw err;
    }
  };

  const updateUser = (newUserData: SessionUser) => {
    setUser(newUserData);
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    updateUser,
    updatePassword,
    updateEmail,
    updateName,
    updatePreferences,
    deleteAccount,
    fetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
